// TKOS Lead-Gen Wizard — shell.
//
// `mount(rootEl, retailerId)` is the entire entry. It:
//   1. Renders a "loading" placeholder while branding loads.
//   2. Fetches retailer branding and applies it as CSS variables.
//   3. Builds the header + progress dots + footer.
//   4. Owns the canonical `WizardState`, exposes mutators to each step.
//   5. Mounts the current step into the main slot; advances on `next()`.
//
// State is in-memory only — no localStorage. Page reload restarts the flow.

import type { WizardState } from "../types";
import { emptyState } from "../types";
import { applyBranding, loadBranding } from "./branding-loader";
import { mountSplash } from "./splash";
import { mountAria } from "./aria";
import { flush as flushAnalytics, track } from "./analytics";
import { mountStep1 } from "./step-1-room";
import { mountStep2 } from "./step-2-style";
import { mountStep3 } from "./step-3-info";
import { mountStep4 } from "./step-4-review";

export interface StepContext {
  state: WizardState;
  /** Replace `state` entirely (deep-replace; caller is responsible for spread). */
  setState: (next: WizardState) => void;
  /** Advance to the given step (0..3), or the next step if not given. */
  goTo: (index: number) => void;
}

const STEP_SHORT_LABELS = [
  "Build Room",
  "Style & Colour",
  "Your Info",
  "Finish",
] as const;

export async function mount(
  rootEl: HTMLElement,
  retailerId: string,
  prefill?: Record<string, string>,
): Promise<void> {
  rootEl.innerHTML = `
    <div class="wiz-app" data-role="app">
      <div class="wiz-status" data-role="status">
        <span class="wiz-spinner" aria-hidden="true"></span>
        <p>Loading…</p>
      </div>
    </div>
  `;

  const app = rootEl.querySelector<HTMLElement>("[data-role=app]")!;
  const status = app.querySelector<HTMLElement>("[data-role=status]")!;

  let state: WizardState = emptyState(retailerId);

  // Seed contact details handed over from the website-chat embed (safe fields
  // only — budget/timeline are picker enums the freeform chat values won't match).
  if (prefill) {
    state.customer.firstName = String(prefill.firstName || prefill.name || "").slice(0, 120) || state.customer.firstName;
    state.customer.lastName = String(prefill.lastName || "").slice(0, 120) || state.customer.lastName;
    state.customer.email = String(prefill.email || "").slice(0, 200) || state.customer.email;
    state.customer.phone = String(prefill.phone || "").slice(0, 60) || state.customer.phone;
    // Kitchen layout + island captured by the website chat AI → pre-select tiles.
    const lt = String((prefill as Record<string, unknown>).layout_type || "");
    if (lt === "single_wall" || lt === "galley" || lt === "l_shape" || lt === "u_shape") {
      state.room.layoutType = lt;
    }
    const hi = (prefill as Record<string, unknown>).has_island;
    if (typeof hi === "boolean") state.room.hasIsland = hi;
    else if (hi === "true" || hi === "false") state.room.hasIsland = hi === "true";
  }

  try {
    const branding = await loadBranding(retailerId);
    state = { ...state, branding };
    applyBranding(branding);
  } catch {
    // applyBranding is also called below with defaults to make sure CSS vars exist.
    applyBranding(state.branding);
  }

  // Build the real shell.
  status.remove();
  app.innerHTML = `
    <header class="wiz-header" data-role="header"></header>
    <main class="wiz-main">
      <div data-role="step-host"></div>
    </main>
    <footer class="wiz-powered" data-role="powered"></footer>
  `;

  const header = app.querySelector<HTMLElement>("[data-role=header]")!;
  const stepHost = app.querySelector<HTMLElement>("[data-role=step-host]")!;
  const powered = app.querySelector<HTMLElement>("[data-role=powered]")!;

  // Aria conversational mode (behind ?aria=1) replaces the form steps + step header.
  const ariaMode = new URLSearchParams(window.location.search).get("aria") === "1";
  if (!ariaMode) renderHeader(header, state);
  renderPowered(powered, state);

  const ctx: StepContext = {
    state,
    setState: (next) => {
      ctx.state = next;
      state = next;
    },
    goTo: (index) => {
      const prevIndex = ctx.state.stepIndex;
      // Emit a step_completed marker whenever the user moves forward off a
      // step (back-navigation is not counted as a completion).
      if (index > prevIndex) {
        track({ eventType: "step_completed", value: String(prevIndex + 1) });
      }
      ctx.state = { ...ctx.state, stepIndex: index };
      state = ctx.state;
      renderHeader(header, state);
      renderStep(stepHost, ctx);
      // Flush queued events on every step transition so the supplier portal
      // sees engagement data without waiting for unload.
      flushAnalytics();
      // Reset scroll so each step starts at the top.
      try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch { /* ignore */ }
    },
  };

  if (ariaMode) mountAria(stepHost, ctx); else renderStep(stepHost, ctx);

  // Step 1 — the welcome / capture surface. Full-viewport, white-label,
  // retailer-branded; sits over the shell until the customer has given us a
  // name and email, then leaves to reveal the room step underneath.
  mountSplash(app, state.branding, retailerId, (captured) => {
    ctx.setState({
      ...ctx.state,
      customer: {
        ...ctx.state.customer,
        // Don't clobber a value the website-chat embed already handed over
        // with a blank — but what the customer just typed here wins.
        firstName: captured.firstName || ctx.state.customer.firstName,
        email: captured.email || ctx.state.customer.email,
      },
      accountCaptured: true,
    });
    // Step 1 was already mounted underneath the splash, so it still shows the
    // pre-capture bottom bar ("Login" rather than "Logout"). Re-render it now
    // that we know who the customer is. Aria mode owns its own surface.
    if (!ariaMode) renderStep(stepHost, ctx);
    try { window.scrollTo({ top: 0 }); } catch { /* ignore */ }
  });
}

function renderHeader(host: HTMLElement, state: WizardState): void {
  const steps = STEP_SHORT_LABELS.map((label, i) => {
    const state_ = i === state.stepIndex
      ? "wiz-step--active"
      : i < state.stepIndex
        ? "wiz-step--done"
        : "wiz-step--upcoming";
    return `
      <div class="wiz-step ${state_}" aria-current="${i === state.stepIndex ? "step" : "false"}">
        <span class="wiz-step__num">${i + 1}</span>
        <span class="wiz-step__label">${escape(label)}</span>
      </div>
    `;
  }).join('<div class="wiz-step__sep" aria-hidden="true"></div>');

  host.innerHTML = `
    <div class="wiz-header__steps" data-role="steps">${steps}</div>
    <button class="wiz-fullscreen-btn" type="button" data-action="fullscreen" aria-label="Toggle fullscreen" title="Toggle fullscreen">
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path d="M8 3H5a2 2 0 0 0-2 2v3"/><path d="M16 3h3a2 2 0 0 1 2 2v3"/>
        <path d="M8 21H5a2 2 0 0 1-2-2v-3"/><path d="M16 21h3a2 2 0 0 0 2-2v-3"/>
        <path d="M3 9l6-6"/><path d="M21 9l-6-6"/><path d="M3 15l6 6"/><path d="M21 15l-6 6"/>
      </svg>
    </button>
  `;

  host.querySelector<HTMLButtonElement>('[data-action=fullscreen]')
    ?.addEventListener("click", async () => {
      try {
        if (document.fullscreenElement) {
          await document.exitFullscreen();
        } else {
          await (document.documentElement.requestFullscreen?.() ?? Promise.resolve());
        }
      } catch {
        // Fullscreen can be denied by the browser/environment; keep the wizard usable.
      }
    });
}

function renderPowered(host: HTMLElement, state: WizardState): void {
  if (state.branding.showPoweredBy) {
    host.innerHTML = `Powered by <a href="https://thekitchenos.io" target="_blank" rel="noopener">Kitchengine</a>`;
  } else {
    host.innerHTML = "";
  }
}

function renderStep(host: HTMLElement, ctx: StepContext): void {
  host.innerHTML = `<div class="wiz-step" data-role="step"></div>`;
  const slot = host.querySelector<HTMLElement>("[data-role=step]")!;
  switch (ctx.state.stepIndex) {
    case 0: mountStep1(slot, ctx); return;
    case 1: mountStep2(slot, ctx); return;
    case 2: mountStep3(slot, ctx); return;
    case 3: mountStep4(slot, ctx); return;
    default:
      ctx.goTo(0);
  }
}

// ── HTML escape helpers (shared by all steps) ────────────────────────────────

export function escape(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

export function escapeAttr(s: string): string {
  return escape(s);
}

// ── Start-planning / login popup (Bug 9) ─────────────────────────────────────
// Triggered from the bottom-bar "Login" control. Offers two paths: keep
// planning here (new visitor), or log in to an existing customer portal.
// Replaces the old bare `<a href="/customer-portal">`, which 404'd inside the
// `/wizard/` host.

/** Best-effort customer-portal URL derived from the host (dev vs prod). */
function customerPortalLoginUrl(): string {
  try {
    const h = window.location.hostname;
    if (h.startsWith("dev") || h.includes("dev-")) return "https://dev-customer.tkos.io";
  } catch {
    /* ignore — fall through to prod default */
  }
  return "https://customer.tkos.io";
}

// ── Bottom-bar auth control ──────────────────────────────────────────────────
// Step 1 captures a name + email up front, so by the time the customer reaches
// the later steps the "Login" affordance is wrong — they're already known to
// us. The control therefore flips to "Logout" once `accountCaptured` is set.
// Shared by every step's bottom bar so the two can never drift apart.

const ICON_USER =
  `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>`;
const ICON_LOGOUT =
  `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><path d="M16 17l5-5-5-5"/><path d="M21 12H9"/></svg>`;

/** Markup for the bottom bar's Login/Logout control, chosen from state. */
export function authBarButtonHtml(state: WizardState): string {
  if (state.accountCaptured) {
    return `<button type="button" class="wiz-bb-btn" data-action="logout" title="Log out">
        ${ICON_LOGOUT}<span>Logout</span>
      </button>`;
  }
  return `<a class="wiz-bb-btn" data-action="login" href="/customer-portal" title="Customer portal login">
      ${ICON_USER}<span>Login</span>
    </a>`;
}

/** Wire whichever of the two the step rendered. */
export function wireAuthBarButton(host: HTMLElement, ctx: StepContext): void {
  host.querySelector<HTMLAnchorElement>("[data-action=login]")
    ?.addEventListener("click", (e) => { e.preventDefault(); openStartLoginPopup(); });
  host.querySelector<HTMLButtonElement>("[data-action=logout]")
    ?.addEventListener("click", () => openLogoutPopup(ctx));
}

/** Confirm-then-logout. The wizard holds its state in memory only, so logging
 *  out genuinely means starting over — say so before doing it, rather than
 *  silently binning what they've filled in. */
export function openLogoutPopup(ctx: StepContext): void {
  if (document.getElementById("wiz-logout-popup")) return;
  const name = ctx.state.customer.firstName.trim();
  const overlay = document.createElement("div");
  overlay.id = "wiz-logout-popup";
  overlay.className = "wiz-capture";
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-modal", "true");
  overlay.setAttribute("aria-label", "Log out");
  overlay.innerHTML = `
    <div class="wiz-capture__panel wiz-capture__panel--narrow">
      <h2 class="wiz-capture__title">Log out${name ? `, ${escape(name)}` : ""}?</h2>
      <p class="wiz-capture__sub">
        Your answers are only held on this device while you're planning, so logging
        out will clear them and start you over. Your details are already safe with
        your retailer.
      </p>
      <button type="button" class="wiz-capture__submit" data-role="confirm">Log out</button>
      <button type="button" class="wiz-capture__secondary" data-role="cancel">Keep planning</button>
    </div>
  `;

  const onKey = (e: KeyboardEvent): void => { if (e.key === "Escape") close(); };
  function close(): void {
    overlay.remove();
    document.removeEventListener("keydown", onKey);
  }
  overlay.addEventListener("click", (e) => { if (e.target === overlay) close(); });
  overlay.querySelector("[data-role=cancel]")?.addEventListener("click", close);
  overlay.querySelector("[data-role=confirm]")?.addEventListener("click", () => {
    close();
    // In-memory state + a fresh load == a clean session, back at the welcome.
    try { window.location.reload(); } catch { /* ignore */ }
  });
  document.addEventListener("keydown", onKey);
  document.body.appendChild(overlay);
  (overlay.querySelector("[data-role=cancel]") as HTMLElement | null)?.focus();
}

export function openStartLoginPopup(): void {
  if (document.getElementById("wiz-login-popup")) return; // no duplicates
  const overlay = document.createElement("div");
  overlay.id = "wiz-login-popup";
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-modal", "true");
  overlay.setAttribute("aria-label", "Start planning or log in");
  overlay.style.cssText =
    "position:fixed;inset:0;z-index:9999;display:flex;align-items:center;justify-content:center;" +
    "background:rgba(10,10,12,0.66);padding:20px;";
  overlay.innerHTML = `
    <div style="position:relative;background:#1b1b1f;border:1px solid #2a2a2f;border-radius:16px;max-width:420px;width:100%;padding:28px 26px;box-shadow:0 24px 60px rgba(0,0,0,0.5);text-align:center;">
      <button type="button" data-role="close" aria-label="Close" style="position:absolute;top:14px;right:16px;border:0;background:transparent;color:#888;font-size:24px;line-height:1;cursor:pointer;">&times;</button>
      <h2 style="margin:0 0 8px;font-size:20px;font-weight:800;color:#f5f5f5;">Start planning your kitchen</h2>
      <p style="margin:0 0 22px;font-size:14px;line-height:1.6;color:#a0a0a8;">Keep designing here — your progress saves automatically. Already have a customer portal? Log in to pick up where you left off.</p>
      <button type="button" data-role="start" style="display:block;width:100%;margin:0 0 10px;padding:13px 18px;border:0;border-radius:10px;background:var(--brand-primary);color:var(--brand-text);font-size:15px;font-weight:700;cursor:pointer;">Start planning</button>
      <button type="button" data-role="login" style="display:block;width:100%;padding:12px 18px;border:1px solid #3a3a40;border-radius:10px;background:transparent;color:#e0e0e6;font-size:14px;font-weight:600;cursor:pointer;">Log in to your portal</button>
    </div>
  `;

  const onKey = (e: KeyboardEvent): void => {
    if (e.key === "Escape") close();
  };
  function close(): void {
    overlay.remove();
    document.removeEventListener("keydown", onKey);
  }

  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) close();
  });
  overlay.querySelector("[data-role=start]")?.addEventListener("click", close);
  overlay.querySelector("[data-role=close]")?.addEventListener("click", close);
  overlay.querySelector("[data-role=login]")?.addEventListener("click", () => {
    const url = customerPortalLoginUrl();
    // Break out of the iframe when the wizard is embedded in a retailer site.
    try { window.top!.location.href = url; } catch { window.location.href = url; }
  });
  document.addEventListener("keydown", onKey);
  document.body.appendChild(overlay);
  (overlay.querySelector("[data-role=start]") as HTMLElement | null)?.focus();
}

// ── Chat enquiry popup ───────────────────────────────────────────────────────
// Triggered from the bottom-bar "Chat" control. Captures name/phone/email +
// the customer's question, POSTs to /api/public/wizard-enquiry which creates a
// CRM lead (deduped by email) and drops the message into the retailer's
// Messages thread. The customer gets an immediate acknowledgement SMS, and the
// retailer's reply comes back to them by SMS.

const CHAT_POLL_MS = 4000;

// Light form fields for the chat widget (matches the source-of-truth consumer
// live-chat: white inputs, uppercase grey labels).
const CHAT_FIELD_CSS =
  "width:100%;box-sizing:border-box;background:#fff;border:1.5px solid #e2e8f0;" +
  "border-radius:10px;padding:10px 12px;color:#1e293b;font:inherit;font-size:14px;outline:none;";

function chatField(field: string, label: string, type: string, value: string): string {
  return `<label style="display:block;margin:0 0 12px;">
    <span style="display:block;font-size:11px;font-weight:700;letter-spacing:.02em;text-transform:uppercase;color:#64748b;margin:0 0 5px;">${escape(label)}</span>
    <input data-field="${escapeAttr(field)}" type="${escapeAttr(type)}" value="${escapeAttr(value)}" style="${CHAT_FIELD_CSS}">
  </label>`;
}

// Inject the slide-up keyframes once (cubic-bezier overshoot, matching the
// source-of-truth chatPopIn entrance).
function ensureChatKeyframes(): void {
  if (document.getElementById("wiz-chat-kf")) return;
  const st = document.createElement("style");
  st.id = "wiz-chat-kf";
  st.textContent =
    "@keyframes wizChatSlideUp{from{opacity:0;transform:translateY(24px) scale(.98)}" +
    "to{opacity:1;transform:translateY(0) scale(1)}}";
  document.head.appendChild(st);
}

// One chat bubble. Orange "me" (customer, right) / grey "them" (retailer, left).
// Optimistic "me" bubbles carry data-pending+data-text so a later server poll
// can reconcile them instead of rendering a duplicate.
function chatBubble(from: "me" | "them", text: string, pending?: boolean): HTMLElement {
  const el = document.createElement("div");
  el.style.cssText = from === "me"
    ? "align-self:flex-end;max-width:85%;padding:10px 14px;background:var(--brand-primary);color:var(--brand-text);" +
      "border-radius:14px 14px 2px 14px;font-size:13px;line-height:1.5;box-shadow:0 4px 12px color-mix(in srgb, var(--brand-primary) 15%, transparent);overflow-wrap:anywhere;"
    : "align-self:flex-start;max-width:85%;padding:10px 14px;background:#eef1f5;color:#1e293b;" +
      "border:1px solid #e2e8f0;border-radius:14px 14px 14px 2px;font-size:13px;line-height:1.5;overflow-wrap:anywhere;";
  if (from === "me" && pending) { el.dataset.pending = "1"; el.dataset.text = text; }
  el.textContent = text;
  return el;
}

// ── Live chat widget ─────────────────────────────────────────────────────────
// Bottom-right fixed widget. Opens on the contact form, POSTs the enquiry to
// /api/public/wizard-enquiry (which returns a chatToken), then transitions into
// a live chat view: polls /api/public/wizard-chat/:token every 4s for the
// retailer's replies and lets the customer keep talking without leaving the
// wizard. The retailer still gets the immediate SMS ack as an offline fallback.
export function openChatPopup(ctx: StepContext): void {
  if (document.getElementById("wiz-chat-popup")) return; // no duplicates
  ensureChatKeyframes();
  const c = ctx.state.customer;
  const fullName = [c.firstName, c.lastName].filter(Boolean).join(" ").trim();

  const widget = document.createElement("div");
  widget.id = "wiz-chat-popup";
  widget.setAttribute("role", "dialog");
  widget.setAttribute("aria-label", "Live chat");
  widget.style.cssText =
    "position:fixed;bottom:20px;right:20px;z-index:9999;width:min(360px,calc(100vw - 24px));" +
    "max-height:min(70vh,560px);display:flex;flex-direction:column;overflow:hidden;" +
    "background:#fff;border-radius:16px;box-shadow:0 18px 55px rgba(0,0,0,0.32);" +
    "font-family:inherit;animation:wizChatSlideUp .28s cubic-bezier(0.175,0.885,0.32,1.275);";

  widget.innerHTML = `
    <div style="flex:0 0 auto;padding:14px 16px;background:linear-gradient(135deg,var(--brand-primary),#f59e0b);color:var(--brand-text);display:flex;justify-content:space-between;align-items:center;">
      <div style="display:flex;align-items:center;gap:10px;">
        <span style="width:9px;height:9px;border-radius:50%;background:#4ade80;box-shadow:0 0 0 3px rgba(74,222,128,0.25);flex:0 0 auto;"></span>
        <div style="line-height:1.25;">
          <div style="font-size:14px;font-weight:800;">Live chat</div>
          <div style="font-size:10.5px;font-weight:600;opacity:.92;">We'll reply here — and text you too</div>
        </div>
      </div>
      <button type="button" data-role="close" aria-label="Close" style="border:0;background:transparent;color:#fff;font-size:24px;line-height:1;cursor:pointer;padding:0 2px;">&times;</button>
    </div>

    <div data-role="form" style="flex:1 1 auto;overflow-y:auto;padding:18px 16px;">
      <p style="margin:0 0 14px;font-size:13px;line-height:1.5;color:#475569;">Send your question and chat with our kitchen team in real time.</p>
      ${chatField("name", "Your name", "text", fullName)}
      ${chatField("phone", "Phone number", "tel", c.phone)}
      ${chatField("email", "Email address", "email", c.email)}
      <label style="display:block;margin:0 0 14px;">
        <span style="display:block;font-size:11px;font-weight:700;letter-spacing:.02em;text-transform:uppercase;color:#64748b;margin:0 0 5px;">Your question</span>
        <textarea data-field="message" rows="3" style="${CHAT_FIELD_CSS}resize:vertical;"></textarea>
      </label>
      <button type="button" data-role="send" style="display:block;width:100%;padding:13px 18px;border:0;border-radius:12px;background:var(--brand-primary);color:var(--brand-text);font-size:15px;font-weight:800;cursor:pointer;box-shadow:0 4px 12px color-mix(in srgb, var(--brand-primary) 20%, transparent);">Start chatting</button>
      <p data-role="err" style="display:none;margin:10px 0 0;font-size:12.5px;color:#dc2626;"></p>
    </div>

    <div data-role="chat" style="display:none;flex:1 1 auto;min-height:0;flex-direction:column;">
      <div data-role="msgs" style="flex:1 1 auto;min-height:0;overflow-y:auto;padding:16px;background:#f8fafc;display:flex;flex-direction:column;gap:10px;"></div>
      <div style="flex:0 0 auto;padding:12px;border-top:1px solid #e2e8f0;background:#fff;display:flex;gap:8px;align-items:center;">
        <input data-role="reply" type="text" placeholder="Type a message…" style="flex:1 1 auto;min-width:0;background:#fff;border:1.5px solid #e2e8f0;border-radius:10px;padding:10px 12px;color:#1e293b;font:inherit;font-size:13px;outline:none;">
        <button type="button" data-role="reply-send" aria-label="Send" style="flex:0 0 auto;width:40px;height:40px;border:0;border-radius:10px;background:var(--brand-primary);color:var(--brand-text);display:flex;align-items:center;justify-content:center;cursor:pointer;">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h13.5M12.5 5.5 19 12l-6.5 6.5"/></svg>
        </button>
      </div>
    </div>
  `;

  let pollTimer: number | undefined;
  let token: string | null = null;
  const seen = new Set<string>();

  const onKey = (e: KeyboardEvent): void => { if (e.key === "Escape") close(); };
  function close(): void {
    if (pollTimer !== undefined) window.clearInterval(pollTimer);
    widget.remove();
    document.removeEventListener("keydown", onKey);
  }
  const q = <T extends HTMLElement>(sel: string): T => widget.querySelector(sel) as T;
  const val = (f: string): string =>
    (widget.querySelector<HTMLInputElement | HTMLTextAreaElement>(`[data-field="${f}"]`)?.value ?? "").trim();

  const msgsBox = q<HTMLElement>("[data-role=msgs]");
  const errEl = q<HTMLElement>("[data-role=err]");
  const showErr = (m: string): void => { errEl.textContent = m; errEl.style.display = "block"; };

  // Merge server messages into the thread without duplicating optimistic sends.
  function renderServer(messages: Array<{ id: string; from: string; text: string }>): void {
    let appended = false;
    for (const m of messages) {
      if (!m || !m.id || seen.has(m.id)) continue;
      if (m.from === "me") {
        const pend = Array.from(msgsBox.querySelectorAll<HTMLElement>('[data-pending="1"]'))
          .find((el) => el.dataset.text === m.text);
        if (pend) { delete pend.dataset.pending; seen.add(m.id); continue; }
      }
      msgsBox.appendChild(chatBubble(m.from === "me" ? "me" : "them", m.text));
      seen.add(m.id);
      appended = true;
    }
    if (appended) msgsBox.scrollTop = msgsBox.scrollHeight;
  }

  async function poll(): Promise<void> {
    if (!token) return;
    try {
      const res = await fetch(`/api/public/wizard-chat/${encodeURIComponent(token)}`);
      if (!res.ok) return;
      const data = await res.json();
      if (data && Array.isArray(data.messages)) renderServer(data.messages);
    } catch { /* transient — retry next tick */ }
  }

  function startChat(firstMessage: string): void {
    q<HTMLElement>("[data-role=form]").style.display = "none";
    q<HTMLElement>("[data-role=chat]").style.display = "flex";
    widget.style.height = "min(70vh,560px)";
    // Show the enquiry we just sent as the opening (optimistic) bubble.
    msgsBox.appendChild(chatBubble("me", firstMessage, true));
    msgsBox.scrollTop = msgsBox.scrollHeight;
    if (token) {
      poll();
      pollTimer = window.setInterval(poll, CHAT_POLL_MS);
    } else {
      // No live thread (messaging offline / no contact key) — keep it honest:
      // their message still reached the retailer, who replies by text/email.
      msgsBox.appendChild(chatBubble("them", "Thanks! We'll reply by text and email shortly."));
    }
    q<HTMLInputElement>("[data-role=reply]").focus();
  }

  async function sendReply(): Promise<void> {
    const input = q<HTMLInputElement>("[data-role=reply]");
    const text = input.value.trim();
    if (!text || !token) return;
    input.value = "";
    const bubble = chatBubble("me", text, true);
    msgsBox.appendChild(bubble);
    msgsBox.scrollTop = msgsBox.scrollHeight;
    try {
      const res = await fetch(`/api/public/wizard-chat/${encodeURIComponent(token)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text }),
      });
      if (!res.ok) throw new Error(String(res.status));
      poll();
    } catch {
      bubble.style.opacity = "0.5";
      bubble.title = "Not delivered — please try again";
    }
  }

  const sendBtn = q<HTMLButtonElement>("[data-role=send]");
  sendBtn.addEventListener("click", async () => {
    const name = val("name"), phone = val("phone"), email = val("email"), message = val("message");
    if (!name)            return showErr("Please enter your name.");
    if (!email && !phone) return showErr("Please enter an email or phone number.");
    if (!message)         return showErr("Please enter your question.");
    errEl.style.display = "none";
    sendBtn.disabled = true;
    sendBtn.textContent = "Sending…";
    try {
      const res = await fetch("/api/public/wizard-enquiry", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ retailerId: ctx.state.retailerId, name, phone, email, message }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json().catch(() => ({}));
      token = (data && typeof data.chatToken === "string") ? data.chatToken : null;
      startChat(message);
    } catch {
      sendBtn.disabled = false;
      sendBtn.textContent = "Start chatting";
      showErr("Sorry — couldn't send right now. Please try again.");
    }
  });

  q<HTMLElement>("[data-role=close]").addEventListener("click", close);
  q<HTMLButtonElement>("[data-role=reply-send]").addEventListener("click", () => { sendReply(); });
  q<HTMLInputElement>("[data-role=reply]").addEventListener("keydown", (e) => {
    if (e.key === "Enter") { e.preventDefault(); sendReply(); }
  });

  document.addEventListener("keydown", onKey);
  document.body.appendChild(widget);
  q<HTMLInputElement>('[data-field="name"]').focus();
}
